import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ISquadsStore, ISquadWithResults} from "data/stores/squads/squads.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import type {ITournament, ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ChangeEvent} from "react";
import {get, includes} from "lodash";
import type {IHeavyPredictionsStore} from "data/stores/heavy_predictions/heavy_predictions.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import type {IUserStore} from "data/stores/user/user.store";

interface IProps {
	match: ITournament;
}
export interface IMatchController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get homeSquad(): ISquadWithResults | null;
	get awaySquad(): ISquadWithResults | null;
	get matchValue(): number;

	onSetWinner: (event: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class MatchController implements IMatchController {
	@observable private _match: ITournament | null = null;

	get homeSquad() {
		if (!this._match) return null;

		const squad = this._squadStore.getSquadById(this._match.homeSquadId);
		const squadStanding = this._standingsStore.userStandings.findLast(
			(it) => it.squadId === squad?.id
		);

		return {
			...squad,
			...squadStanding,
		} as ISquadWithResults;
	}

	get awaySquad() {
		if (!this._match) return null;

		const squad = this._squadStore.getSquadById(this._match.awaySquadId);
		const squadStanding = this._standingsStore.userStandings.findLast(
			(it) => it.squadId === squad?.id
		);

		return {
			...squad,
			...squadStanding,
		} as ISquadWithResults;
	}

	get matchValue() {
		const currentRoundId = this._roundStore.selectedRoundId;
		const roundPredictions = get(this._hpStore.predictions, currentRoundId, []);
		const winSquad = roundPredictions.find((it) => it.matchId === this._match?.id);

		return winSquad?.squadId ?? 0;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.HeavyPredictionsStore) private _hpStore: IHeavyPredictionsStore
	) {
		makeAutoObservable(this);
	}

	onSetWinner = (event: ChangeEvent<HTMLInputElement>) => {
		if (!this._match) return;

		const winSquadId = Number(event.target.value);
		const matchesByRound = this._tournamentStore.matchesByRoundId(
			this._roundStore.selectedRoundId
		);
		const match = matchesByRound.find((it) =>
			includes([it.homeSquadId, it.awaySquadId], winSquadId)
		);
		const opponentId =
			winSquadId === match?.homeSquadId ? match.awaySquadId : match?.homeSquadId;

		const predictionBySelectedRound = this._hpStore.predictionBySelectedRoundId;
		const hasPredictionBySquad = predictionBySelectedRound.find(
			(it) => it.squadId === opponentId
		);

		this._hpStore.setPredictionsForRound({
			squadId: winSquadId,
			matchId: this._match.id,
		});

		this._hpStore.setIsPredictionsChanged(true);

		if (hasPredictionBySquad) {
			createConnextraScript(ConnextraType.HP_PICKS_UPDATE, this._userStore.user?.id);
		}
	};

	init({match}: IProps) {
		this._match = match;
	}
}
